import { Suspense, useEffect, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SCard } from "@/components/Card/SCard";
import { getTemplate } from "@/lib/api/handlers/EventTemplateHandling";
import type { EventTemplate } from "@/types/EventTemplate";
import { TemplateSidebar } from "@/pages/templates/template/components/TemplateSidebar";
import { warmCommonTemplateRoutes } from "@/pages/templates/template/components/routePrefetch";
import { SError } from "@/components/Error/SError";
import { InlineLoadingSpinner } from "@/components/LoadingSpinner/InlineLoadingSpinner";
import { CapabilitiesProvider } from "@/providers/CapabilitiesProvider";
import type { EventRoleDTO } from "@/types/EventRoleDTO";

function isServerErr(x: unknown): x is { isServerError: true; message: string } {
  return !!x && typeof x === "object" && "isServerError" in x && (x as { isServerError?: boolean }).isServerError === true;
}

export function TemplateLayoutShell() {
  const { templateId = "" } = useParams<{ templateId: string }>();

  useEffect(() => {
    type WindowWithIdle = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const w = window as WindowWithIdle;
    if (!w.requestIdleCallback) return;
    const id = w.requestIdleCallback(() => warmCommonTemplateRoutes(), {
      timeout: 2000,
    });
    return () => w.cancelIdleCallback?.(id);
  }, []);

  const q = useQuery({
    queryKey: ["templateLayout", templateId],
    queryFn: () => getTemplate(templateId),
    enabled: !!templateId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Stable sentinel for the loading window so TemplateSidebar can paint
  // immediately with section icons + empty name/description, and so its
  // `useEffect(..., [template.includedSections, isToggling])` doesn't loop
  // on a fresh `?? []` ref each render.
  const sentinel = useMemo<EventTemplate>(
    () => ({
      templateId,
      companyId: "",
      name: "",
      includedSections: [],
      createdAt: "",
      updatedAt: "",
      createdBy: "",
    }),
    [templateId],
  );

  // Template routes are OWNER/ADMIN-only at the API layer (@RolesAllowed on
  // every mutating template endpoint). Synthesise an OWNER eventRole so
  // shared event-side components mounted under a template (SenderBody,
  // dashboard tiles, etc.) get a full capability set and don't crash on
  // useCapabilities(). Declared above the early-return so the hook count
  // stays constant across error/non-error renders.
  const templateEventRole: EventRoleDTO = useMemo(
    () => ({ role: "OWNER" }) as EventRoleDTO,
    [],
  );

  // Fatal-error gate only — loading state intentionally falls through so
  // the sidebar paints immediately; only the Outlet waits via Suspense.
  if (q.isError || isServerErr(q.data)) {
    const msg = isServerErr(q.data)
      ? q.data.message
      : q.error instanceof Error
        ? q.error.message
        : "unknown";
    return <SError error={msg} />;
  }

  const template: EventTemplate =
    q.data && !isServerErr(q.data) ? (q.data as EventTemplate) : sentinel;

  return (
    <CapabilitiesProvider eventRole={templateEventRole}>
    <div className="flex flex-col w-full p-4 sm:p-6" style={{ height: "calc(100dvh - 45px)" }}>
      <SCard
        width="w-full"
        height="h-full"
        padding="p-3 md:p-6"
        marginOuter=""
        shadow="shadow-md"
        justify=""
        rounded="rounded-2xl"
        outline="outline-quinary"
        className="overflow-hidden"
      >
        <div className="flex flex-col md:flex-row w-full h-full flex-1 min-h-0">
          <TemplateSidebar
            key={templateId}
            template={template}
            isLoading={q.isLoading}
          />
          <div
            data-testid="page-root"
            className="border-t md:border-t-0 md:border-l outline-tertiary flex-1 overflow-y-auto min-h-0 min-w-0 surface-primary flex flex-col"
          >
            <Suspense fallback={<InlineLoadingSpinner />}>
              <Outlet key={templateId} />
            </Suspense>
          </div>
        </div>
      </SCard>
    </div>
    </CapabilitiesProvider>
  );
}
